import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InvoiceShareService, ShareInvoiceBody } from './invoice-share.service';

export interface ShareInvoiceDialogData {
  invoiceId: string;
  invoiceNumber: string;
  total: string;
}

@Component({
  selector: 'app-share-invoice-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Share invoice</h2>
    <mat-dialog-content>
      <p style="margin: 0 0 16px; opacity: 0.8;">{{ data.invoiceNumber }} · {{ data.total }}</p>

      <mat-form-field appearance="outline" style="width: 100%;">
        <mat-label>Email address</mat-label>
        <input matInput type="email" [(ngModel)]="email" maxlength="254" />
      </mat-form-field>

      <mat-form-field appearance="outline" style="width: 100%;">
        <mat-label>Note (optional)</mat-label>
        <textarea matInput [(ngModel)]="note" maxlength="300" rows="3"></textarea>
      </mat-form-field>

      @if (error()) {
        <p style="color: var(--mat-sys-error); margin: 0;">{{ error() }}</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close [disabled]="sending()">Cancel</button>
      <button mat-flat-button color="primary" (click)="send()" [disabled]="!canSend || sending()">
        {{ sending() ? 'Sending...' : 'Send' }}
      </button>
    </mat-dialog-actions>
  `
})
export class ShareInvoiceDialogComponent {
  data = inject<ShareInvoiceDialogData>(MAT_DIALOG_DATA);
  private ref = inject(MatDialogRef<ShareInvoiceDialogComponent>);
  private shareService = inject(InvoiceShareService);
  private snack = inject(MatSnackBar);

  email = '';
  note = '';
  sending = signal(false);
  error = signal('');

  get canSend(): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
  }

  send(): void {
    if (!this.canSend) return;
    this.sending.set(true);
    this.error.set('');

    const body: ShareInvoiceBody = {
      recipientEmail: this.email.trim(),
      note: this.note.trim() || undefined
    };

    this.shareService.share(this.data.invoiceId, body).subscribe({
      next: () => {
        this.snack.open('Invoice sent by email', 'OK', { duration: 4000 });
        this.ref.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.error.set(
          err.status === 429
            ? 'Too many shares. Please wait a few minutes and try again.'
            : err.error?.message ?? 'Could not share the invoice.'
        );
        this.sending.set(false);
      }
    });
  }
}
